import { useEffect, useReducer } from 'react'
import { sortBy } from 'lodash'
import api, { IPortfolioRegion, IPortfolioBuilding, IPortfolioCity } from '@api'
import { IAssignmentPlan, IBuildingDetails, IPortfolioBuildingDetails } from '@api/types'
import { useAppData } from '@providers'
import { GridTableStatusEnum } from '@ui'
import { useShouldReload } from '@hooks/reloadContext'

enum Actions {
  LOAD,
  LOAD_CITY,
  SET_CITIES,
  CLEAR_FILTERS,
  SET_ASSIGNMENT_PLAN_ID,
  SET_FILTERED_LOCATIONS,
  SET_SHOW_INACTIVE_BUILDINGS,
  DISMISS_NOTIFICATION,
  APPLY_FILTERS,
}

interface IState {
  regions: IPortfolioRegion[]
  assignmentPlanId: string
  primaryAssignmentPlanId: string
  buildings: { [id: string]: IPortfolioBuilding }
  showInactiveBuildings: boolean
  filteredLocations: Array<IPortfolioRegion | IPortfolioCity | IBuildingDetails>
  treeFiltering: { [index: string]: boolean }
  treeExpanding: { [index: string]: boolean }
  normalOccupancy: number
  alertOccupancy: number
  status: GridTableStatusEnum
  cities: string[]
}
export interface IPortfolio extends IState {
  filterCount: () => number
  clearFilters: () => void
  loadCities: ({
    cities,
    assignmentPlanId,
    includeOldBuildings,
    includeDepartment,
  }: {
    cities: string[]
    assignmentPlanId: string
    includeOldBuildings: boolean
    includeDepartment?: boolean
  }) => void
  setAssignmentPlanId: (ap: IAssignmentPlan) => void
  setShowInactiveBuildings: (show: boolean) => void
  setFilteredLocations: (locations: any) => void
  dismissNotification: (id: string, buildingId: string) => void
  stackingEnabled: boolean
  projectionsEnabled: boolean
}

function reducer(state: IState, action: any) {
  switch (action.type) {
    case Actions.LOAD:
      for (const region of action.portfolioRegions) {
        for (const city of region.cities) {
          for (const building of city.buildings) {
            building.floors = sortBy(building.floors, 'floor.story')
          }
        }
      }
      return {
        ...state,
        regions: action.portfolioRegions,
        assignmentPlanId: action.primaryAssignmentPlan.id,
        primaryAssignmentPlanId: action.primaryAssignmentPlan.id,
        status: GridTableStatusEnum.IDLE,
      }
    case Actions.LOAD_CITY:
      const newState = {
        ...state,
        normalOccupancy: action.normalOccupancy,
        alertOccupancy: action.alertOccupancy,
      }
      for (const building of action?.buildings || []) {
        building.floors = sortBy(building.floors, 'floor.story')
        newState.buildings[building.building.id] = building
      }
      return newState
    case Actions.DISMISS_NOTIFICATION:
      const building = state.buildings[action.buildingId]
      building.building.notifications = building.building.notifications.filter(
        (n) => n.id !== action.id
      )
      for (const floor of building.floors)
        floor.floor.notifications = floor.floor.notifications.filter((n) => n.id !== action.id)
      return {
        ...state,
        buildings: {
          ...state.buildings,
          [action.buildingId]: building,
        },
      }
    case Actions.SET_ASSIGNMENT_PLAN_ID:
      return { ...state, assignmentPlanId: action.id }
    case Actions.CLEAR_FILTERS:
      return {
        ...state,
        assignmentPlanId: state.primaryAssignmentPlanId,
        filteredLocations: [],
        showInactiveBuildings: false,
      }
    case Actions.SET_SHOW_INACTIVE_BUILDINGS:
      return { ...state, showInactiveBuildings: action.show }
    case Actions.SET_CITIES:
      return { ...state, cities: [...state.cities, ...action.cities] }
    case Actions.SET_FILTERED_LOCATIONS:
      const locations = action.locations.reduce((acc: any, l: any) => {
        return { ...acc, [l.id || l.name]: true }
      }, {})
      const treeFiltering: { [k: string]: boolean } = {}
      const treeExpanding: { [k: string]: boolean } = {}
      for (const region of state.regions) {
        for (const city of region.cities) {
          for (const building of city.buildings) {
            if (!locations[building.id] && !locations[city.id] && !locations[region.id]) continue
            treeFiltering[building.id] = true
            treeFiltering[city.id] = true
            treeFiltering[region.id] = true

            if (!locations[building.id]) continue
            treeExpanding[building.id] = true
            treeExpanding[city.id] = true
            treeExpanding[region.id] = true
          }
          if (!locations[city.id] && !locations[region.id]) continue
          treeFiltering[city.id] = true
          treeFiltering[region.id] = true

          if (!locations[city.id]) continue
          treeExpanding[city.id] = true
          treeExpanding[region.id] = true
        }
        if (!locations[region.id]) continue
        treeFiltering[region.id] = true
        treeExpanding[region.id] = true
      }
      return {
        ...state,
        treeFiltering,
        treeExpanding,
        filteredLocations: action.locations,
      }
    case Actions.APPLY_FILTERS:
      return {
        ...state,
        ...action.filters,
      }

    default:
      return state
  }
}

function isPortfolioCity(
  item: IPortfolioRegion | IPortfolioCity | IPortfolioBuildingDetails
): item is IPortfolioCity {
  return (item as IPortfolioCity).buildings !== undefined
}

function isPortfolioBuilding(
  type: IPortfolioRegion | IPortfolioCity | IPortfolioBuildingDetails
): type is IPortfolioBuildingDetails {
  return (type as IPortfolioBuildingDetails).floors !== undefined
}

export default function usePortfolio(): IPortfolio {
  const shouldReload = useShouldReload()

  const [state, dispatch] = useReducer(reducer, {
    assignmentPlanId: '',
    primaryAssignmentPlanId: '',
    buildings: {},
    regions: [],
    showInactiveBuildings: false,
    alertOccupancy: 95,
    normalOccupancy: 85,
    filteredLocations: [],
    treeFiltering: {},
    treeExpanding: {},
    notifications: {},
    status: GridTableStatusEnum.LOADING,
    cities: [],
  })
  const {
    product: { features },
  } = useAppData()

  useEffect(() => {
    if (state.regions.length) {
      const storedFilters = JSON.parse(localStorage.getItem('portfolio-location_filter') || 'null')

      if (storedFilters) {
        dispatch({ type: Actions.APPLY_FILTERS, filters: storedFilters })

        const cities: string[] = []

        storedFilters.filteredLocations?.forEach(
          (item: IPortfolioRegion | IPortfolioCity | IPortfolioBuildingDetails) => {
            if (isPortfolioCity(item) || isPortfolioBuilding(item)) {
              if (isPortfolioCity(item)) {
                cities.push(item.name)
              }

              if (isPortfolioBuilding(item)) {
                cities.push(item.city)
              }
            }
          }
        )

        if (cities.length) {
          dispatch({ type: Actions.SET_CITIES, cities })
        }
      }
    }
  }, [state.regions])

  useEffect(() => {
    if (state.regions.length) {
      localStorage.setItem(
        'portfolio-location_filter',
        JSON.stringify({
          filteredLocations: state.filteredLocations,
          treeFiltering: state.treeFiltering,
          treeExpanding: state.treeExpanding,
        })
      )
    }
  }, [state.filteredLocations, state.treeFiltering, state.treeExpanding, state.regions])

  const loadRegions = () => {
    api.portfolio.regions({ showInactiveBuildings: state.showInactiveBuildings }).then(
      ({
        data: {
          viewer: { company },
        },
      }) => dispatch({ type: Actions.LOAD, ...company })
    )
  }

  useEffect(loadRegions, [state.showInactiveBuildings, shouldReload.portfolio])

  useEffect(() => {
    state.cities.forEach((city: string) => {
      api.portfolio
        .cities({
          assignmentPlanId: state.assignmentPlanId,
          cities: [city],
          includeOldBuildings: state.showInactiveBuildings,
          includeDepartment: true,
        })
        .then(
          ({
            data: {
              viewer: {
                company: { portfolio },
              },
            },
          }) => {
            dispatch({ type: Actions.LOAD_CITY, ...portfolio })
          }
        )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.assignmentPlanId, state.showInactiveBuildings])

  return {
    ...state,
    loadCities: ({ cities }) => {
      cities.forEach((city: string) => {
        api.portfolio
          .cities({
            assignmentPlanId: state.assignmentPlanId,
            cities: [city],
            includeOldBuildings: state.showInactiveBuildings,
            includeDepartment: true,
          })
          .then(
            ({
              data: {
                viewer: {
                  company: { portfolio },
                },
              },
            }) => {
              dispatch({ type: Actions.LOAD_CITY, ...portfolio })
            }
          )
      })

      dispatch({ type: Actions.SET_CITIES, cities })
    },
    setAssignmentPlanId: ({ id }: { id: string }) => {
      dispatch({ type: Actions.SET_ASSIGNMENT_PLAN_ID, id })
    },
    setShowInactiveBuildings: (show) =>
      dispatch({ type: Actions.SET_SHOW_INACTIVE_BUILDINGS, show }),
    setFilteredLocations: (locations) =>
      dispatch({ type: Actions.SET_FILTERED_LOCATIONS, locations }),
    filterCount: () => {
      let count = 0
      if (state.assignmentPlanId !== state.primaryAssignmentPlanId) count++
      if (state.filteredLocations.length) count++
      if (state.showInactiveBuildings) count++
      return count
    },
    clearFilters: () => dispatch({ type: Actions.CLEAR_FILTERS }),
    dismissNotification: (id: string, buildingId: string) =>
      dispatch({ type: Actions.DISMISS_NOTIFICATION, id, buildingId }),
    stackingEnabled: features.includes('stacking'),
    projectionsEnabled: features.includes('projection'),
  }
}
